import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ChatPanel } from "llmasaservice-ui";
import {
  materialDark,
  materialLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { Helmet } from "react-helmet";

const EmbedChatComponent: React.FC = () => {
  const [followOnPrompt, setFollowOnPrompt] = useState<string>("");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const id = searchParams.get("id") || "";
  const customer_id = searchParams.get("customer_id") || "";
  const title = searchParams.get("title")
    ? decodeURIComponent(searchParams.get("title") ?? "")
    : "";
  const template = searchParams.get("template")
    ? decodeURIComponent(searchParams.get("template") ?? "")
    : "";
  const initial = searchParams.get("initial")
    ? decodeURIComponent(searchParams.get("initial") ?? "")
    : "I'm here to help. What can I do for you today?";
  const themeParam = searchParams.get("theme");
  const theme: "dark" | "light" | undefined =
    themeParam === "dark" || themeParam === "light" ? themeParam : "dark";
  const start = searchParams.get("start") ?? "message";

  const followOn = searchParams.get("followon") ?? "";

  useEffect(() => {
    if (followOn && followOn !== "") {
      setFollowOnQuestions(followOn.split("|"));
    }
  },
    [followOn]);

  const openUrlActionCallback = (match: string, groups: any[]) => {
    // is match a url?
    if (match.startsWith("http") || match.startsWith("mailto")) {
      window.open(match, "_blank");
    }

    // are any groups urls?
    groups.forEach((group) => {
      // if group is a string and starts with http or mailto, open it in a new tab
      if (
        typeof group === "string" &&
        (group.toLowerCase().startsWith("http") ||
          group.toLowerCase().startsWith("mailto"))
      ) {
        window.open(group, "_blank");
      }
    });
  };

  const copyToClipboardCallback = (match: string, groups: any[]) => {
    if (groups && groups.length > 0) {
      navigator.clipboard.writeText(groups[0]);
      return;
    }

    navigator.clipboard.writeText(match);
  };

  const showAlertCallback = (match: string, groups: any[]) => {
    if (groups && groups.length > 0) {
      alert(groups[0]);
      return;
    }

    alert(match);
  };

  const sendFollowOnPromptCallback = (match: string, groups: any[]) => {
    console.log("sendFollowOnPromptCallback", match, groups);
    if (groups && groups.length > 0) {
      setFollowOnPrompt(groups[0]);
      return;
    }
    setFollowOnPrompt(match);
  };

  const [followOnQuestions, setFollowOnQuestions] = useState<string[]>([]);

  const setFollowUpQuestionsCallback = (match: string, groups: any[]) => {
    if (groups && groups.length > 0) {
      setFollowOnQuestions(groups);
      return;
    }
    setFollowOnQuestions([match]);
  };

  const [initialPrompt, setInitialPrompt] = useState<string | undefined>(
    start === "prompt" ? initial : undefined
  );

  // Create a mapping of callback names to function references
  const callbackMapping: {
    [key: string]: (match: string, groups: any[]) => void;
  } = {
    openUrlActionCallback,
    copyToClipboardCallback,
    showAlertCallback,
    sendFollowOnPromptCallback,
    setFollowUpQuestionsCallback,
  };

  const actionsParam = searchParams.get("actions");
  let actions: any[] = [];
  if (actionsParam) {
    try {
      const decodedActionsParam = decodeURIComponent(actionsParam);
      actions = JSON.parse(decodedActionsParam);
      if (!Array.isArray(actions)) {
        throw new Error("Parsed actions is not an array");
      }

      actions = actions.map((action) => {
        if (
          typeof action.callback === "string" &&
          callbackMapping[action.callback]
        ) {
          return {
            ...action,
            callback: callbackMapping[action.callback],
          };
        }
        return action;
      });
    } catch (error) {
      console.error("Failed to parse actions:", error, actionsParam);
      actions = [];
    }
  }

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift();
    return null;
  };

  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + 30 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    const secure = window.location.protocol === "https:" ? "Secure;" : "";
    document.cookie = `${name}=${value}; ${expires}; ${secure} path=/`;
  };

  return (
    <>
      <Helmet>
        <base target="_blank" />
      </Helmet>
      <ChatPanel
        project_id={id ?? ""}
        initialMessage={start === "message" ? initial : undefined}
        initialPrompt={initialPrompt}
        theme={theme}
        height="100vh"
        width="100vw"
        url={url}
        hideInitialPrompt={start === "prompt" ? true : template !== ""}
        title={title ?? ""}
        customer={{
          customer_id: customer_id ?? "default",
        }}
        promptTemplate={template}
        actions={actions}
        followOnPrompt={followOnPrompt}
        followOnQuestions={followOnQuestions}
        historyChangedCallback={(history) => {
          if (history) {
            setFollowOnPrompt("");
          }
        }}
      />
    </>
  );
};

export default EmbedChatComponent;
